import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { getEnecoBeXapiSiteApiV1OrdersByOrderId } from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Box, Button, Heading, RadioCard, RadioGroup, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useRouter } from 'next/router';
import { Controller, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import DataErrorNotification from '../../../components/DataErrorNotification';
import { ProposalRadioCard } from '../components/ProposalRadioCard';
import { StyledDisclaimerText } from '../components/StyledDisclaimerText';
import { OrderManagementContext } from '../contexts/OrderManagementProvider';
import { useOrderId } from '../hooks/useOrderId';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { syncOrderBasketOrInitiate } from '../utils/syncOrderBasketOrInitiate';

export const BoilerMaintenanceStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { query } = useRouter();
  const { selectedAccount } = useSelfServiceAccount();
  const { language } = useApplication();
  const { setIsLoadingInitialOrder, isLoadingInitialOrder } = useContext(OrderManagementContext);
  const { trackEventBE } = useTracking();

  const [hasUpdateError, setHasUpdateError] = useState(false);

  const orderId = useOrderId();
  const priceSimulationId = query.priceSimulationId as string | undefined;
  const { mutate } = useSWRConfig();

  const { data: orderDataRaw } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    { path: { orderId: orderId ?? '' } },
    [`/orders/${orderId}`],
    !!orderId,
  );
  const orderData = unwrapData(orderDataRaw);

  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<OrderManagementFormSchema>();

  const boilerMaintenanceOptions = useMemo(
    () => fields.items.filter(option => option.fields.data.isAssistanceProposalFlag?.value === false),
    [fields.items],
  );

  const basketBoilerMaintenance = useMemo(() => {
    const optionNames = boilerMaintenanceOptions.map(option => option.name);
    return orderData?.basket?.items?.find(
      item => item.orderProductType === 'NonCommodity' && optionNames.includes(item.technicalProductName ?? ''),
    )?.technicalProductName;
  }, [orderData?.basket?.items, boilerMaintenanceOptions]);

  useEffect(() => {
    if (basketBoilerMaintenance && !getValues('boilerMaintenance')) {
      setValue('boilerMaintenance', basketBoilerMaintenance, { shouldValidate: false });
    }
  }, [basketBoilerMaintenance, getValues, setValue]);

  const getSelectedBoilerMaintenancePrice = (selectedValue: string | undefined): number => {
    if (!selectedValue || selectedValue === 'none') {
      return 0;
    }
    const option = boilerMaintenanceOptions.find(({ name }) => name === selectedValue);
    return option?.fields.data.pricePerMonthVatExclusiveText?.value ?? 0;
  };

  const onNextClick = async () => {
    const isValid = await trigger('boilerMaintenance');
    if (!isValid) {
      return;
    }
    const serviceProposal = getValues('serviceProposal');
    const boilerMaintenance = getValues('boilerMaintenance');

    if (boilerMaintenance === 'none' && !basketBoilerMaintenance) {
      trackEventBE('be_om_select_ketelonderhoud', {
        status: 'success',
        data: getTrackableFormValues(getValues(), {
          boilerMaintenancePrice: getSelectedBoilerMaintenancePrice(boilerMaintenance),
        }),
      });
      nextMainStep();
      return;
    }

    setIsLoadingInitialOrder(true);
    try {
      const newOrderId = await syncOrderBasketOrInitiate({
        orderId,
        orderData,
        boilerMaintenance,
        serviceProposal,
        accountNumber: selectedAccount?.crmAccountNumber ?? '',
        language,
        priceSimulationId,
      });
      if (orderId) {
        mutate(`/orders/${orderId}`);
        mutate(`/orders/${orderId}/simulate`);
      }
      trackEventBE('be_om_select_ketelonderhoud', {
        status: 'success',
        data: getTrackableFormValues(getValues(), {
          boilerMaintenancePrice: getSelectedBoilerMaintenancePrice(boilerMaintenance),
        }),
      });
      nextMainStep(undefined, newOrderId ?? undefined);
    } catch {
      trackEventBE('be_om_select_ketelonderhoud', {
        status: 'failed',
        message: 'Failed to update order with boiler maintenance selection',
        data: getTrackableFormValues(getValues()),
      });
      setHasUpdateError(true);
      setIsLoadingInitialOrder(false);
    }
  };

  return (
    <>
      {hasUpdateError && <DataErrorNotification />}
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.boilerMaintenanceStep.title.value}
        </Heading>
        <div>
          <RichText html={fields.boilerMaintenanceStep.description.value} />
        </div>
      </Stack>
      <Controller
        control={control}
        name="boilerMaintenance"
        render={({ field }) => (
          <RadioGroup
            name="boilerMaintenance"
            aria-labelledby="boilerMaintenance"
            isFullWidth
            value={field.value}
            onValueChange={field.onChange}
            error={errors.boilerMaintenance?.message}>
            {boilerMaintenanceOptions.map(option => {
              const {
                title,
                detailsLink,
                featuresContent,
                badgeLabel,
                priceLabel,
                taxLabel,
                pricePerMonthVatExclusiveText,
                pricePerMonthVatInclusiveText,
              } = option.fields.data;

              return (
                <ProposalRadioCard
                  key={option.name}
                  value={option.name}
                  title={title.value}
                  pricePerMonthVatExclusive={pricePerMonthVatExclusiveText?.value ?? 0}
                  pricePerMonthVatInclusive={pricePerMonthVatInclusiveText?.value ?? 0}
                  priceLabel={priceLabel.value}
                  taxLabel={taxLabel.value}
                  badgeLabel={badgeLabel.value}
                  featuresContent={featuresContent.value}
                  detailsLink={detailsLink.value}
                  type="boilerMaintenance"
                />
              );
            })}
            <RadioCard value="none" ariaLabelledby="none">
              <Box padding="6">
                <Stack direction="row" gap="2" alignY="center">
                  <RadioCard.Indicator />
                  <Text weight="bold" size="BodyL" id="none">
                    {fields.boilerMaintenanceStep.noneOfAboveLabel.value}
                  </Text>
                </Stack>
              </Box>
            </RadioCard>
          </RadioGroup>
        )}
      />
      <Button onClick={onNextClick} isLoading={isLoadingInitialOrder}>
        {fields.navigation.nextButtonLabel.value}
      </Button>

      <StyledDisclaimerText>
        <RichText html={fields.boilerMaintenanceStep.disclaimerContent.value} />
      </StyledDisclaimerText>
    </>
  );
};
